import {type KpiThresholds} from '@/api/reporting';
import {type StatusTone, TONE_BADGE, TONE_EDGE} from '@/lib/status';

export type KpiStatus = 'ok' | 'warning' | 'error';

/**
 The status a KPI's value earns under its thresholds, or null when there is nothing to colour: no
 thresholds declared, or no value. Error wins over warning, so a value past both reads as error.
 */
export function kpiStatus(value: number | null, thresholds?: KpiThresholds): KpiStatus | null {
  if (value === null || thresholds === undefined) {
    return null;
  }

  const crosses = (limit: number | undefined) =>
    limit !== undefined &&
    (thresholds.direction === 'lower_is_worse' ? value <= limit : value >= limit);

  if (crosses(thresholds.error)) {
    return 'error';
  }

  return crosses(thresholds.warning) ? 'warning' : 'ok';
}

// A KPI status is painted like every other status, from lib/status: a filled chip (TONE_BADGE, since
// --success and --warning fail AA as ink) with an accent edge repeating it. Error is tinted, as
// destructive is everywhere else. The word is in the chip too, so colour never carries it alone.
const KPI_STATUS_TONE: Record<KpiStatus, StatusTone> = {
  ok: 'success',
  warning: 'warning',
  error: 'destructive'
};

const KPI_STATUS_LABEL: Record<KpiStatus, string> = {ok: 'OK', warning: 'Warning', error: 'Error'};

function kpiStatusStyle(status: KpiStatus) {
  const tone = KPI_STATUS_TONE[status];
  return {label: KPI_STATUS_LABEL[status], edge: TONE_EDGE[tone], chip: TONE_BADGE[tone]};
}

export const KPI_STATUS_STYLE: Record<KpiStatus, {label: string; edge: string; chip: string}> = {
  ok: kpiStatusStyle('ok'),
  warning: kpiStatusStyle('warning'),
  error: kpiStatusStyle('error')
};
